import type { ChatbotDispatcherResponse } from '../services/chatBotGetAIResponse'
import {
	type AIResponse,
	type CountryIsoTextResponse,
	type FashionAssistantResponse,
	type MessagePullingResponse,
	type PullingLeaveQueuePayload,
	type PullingTextPayload,
	type QuickRepliesResponse,
	type RecommendedProductResponse,
	type ReplyOrderListResponse,
	ResponseType,
} from '../types/AIResponseTypes'
import {
	type AgentMessage,
	type ChatbotMessage,
	MessageRole,
	MessageType,
	type RecommendedProduct,
} from '../types/messageTypes'

const responseTypesMessages: Record<
	ResponseType,
	(args: {
		messageAIContent: AIResponse
		isTimeoutAgentMessage?: boolean
	}) => ChatbotMessage | null
> = {
	[ResponseType.Text]: ({ messageAIContent, isTimeoutAgentMessage }) => {
		const firstResponseText = (messageAIContent as CountryIsoTextResponse)
			.responses[0]
		const value = firstResponseText.text
		if (value) {
			return {
				role: MessageRole.Ai,
				type: MessageType.AIText,
				showAvatar: true,
				value,
				isTimeoutAgentMessage,
			}
		}
		return null
	},
	[ResponseType.ReplyList]: ({ messageAIContent }) => {
		const firstResponseArrayList = (messageAIContent as ReplyOrderListResponse)
			.responses[0]
		const replyList = firstResponseArrayList.elements
		return {
			orders: replyList.map((replyListItem) => ({
				title: replyListItem.title,
				price: replyListItem.totalPrice,
				date: replyListItem.purchaseDate,
				products: replyListItem.products.map((product) => ({
					id: product.productId,
					color: product.color,
				})),
			})),
			showAvatar: false,
			role: MessageRole.Ai,
			type: MessageType.OrderList,
		}
	},
	[ResponseType.QuickReplies]: ({ messageAIContent }) => {
		const firstResponseArrayList = (messageAIContent as QuickRepliesResponse)
			.responses[0]
		const replyList = firstResponseArrayList.elements
		if (replyList[0].type === 'searchAddress') {
			return {
				elements: replyList.map((replyListItem) => ({
					optionValue: replyListItem.title,
					type: replyListItem.type,
				})),
				showAvatar: false,
				role: MessageRole.Ai,
				type: MessageType.SearchAddress,
			}
		}
		return {
			elements: replyList.map((replyListItem, index) => ({
				feedback: index === 0 ? 'positive' : 'negative',
				optionValue: replyListItem.title,
			})),
			showAvatar: false,
			role: MessageRole.Ai,
			type: MessageType.QuickReplies,
		}
	},
	[ResponseType.FashionAssistant]: ({ messageAIContent }) => {
		const firstResponseArrayList = (
			messageAIContent as FashionAssistantResponse
		).responses[0]
		const lookRecommendations = firstResponseArrayList.recommendations

		if (!lookRecommendations) {
			return null
		}

		let recommendations: RecommendedProduct[][] | RecommendedProduct[]

		function isLooksRecommendationResponse(
			data: RecommendedProductResponse[] | RecommendedProductResponse[][]
		): data is RecommendedProductResponse[][] {
			return Array.isArray(data) && data.every((item) => Array.isArray(item))
		}

		if (isLooksRecommendationResponse(lookRecommendations)) {
			recommendations = lookRecommendations.map((lookRecommendation) => {
				return lookRecommendation.map((recommendation) => ({
					id: recommendation.productId,
					name: recommendation.name,
					image: recommendation.images.main,
					url: recommendation.url,
					colorId: recommendation.color.id,
				}))
			})
		} else {
			recommendations = lookRecommendations.map((lookRecommendation) => {
				return {
					id: lookRecommendation.productId,
					name: lookRecommendation.name,
					image: lookRecommendation.images.main,
					url: lookRecommendation.url,
					colorId: lookRecommendation.color.id,
				}
			})
		}

		return {
			recommendations,
			showAvatar: false,
			role: MessageRole.Ai,
			type: MessageType.FashionAssistant,
		}
	},
}

export const getAIResponseMessages = ({
	response,
	isTimeoutAgentMessage = true,
}: {
	response: ChatbotDispatcherResponse
	isTimeoutAgentMessage?: boolean
}): ChatbotMessage[] | null => {
	const result = response.queryResult.responseMessages.reduce(
		(acc: ChatbotMessage[], responseMessage) => {
			const messageAIContent = responseMessage.payload

			const newResponse = responseTypesMessages[messageAIContent.type]?.({
				messageAIContent,
				isTimeoutAgentMessage,
			})
			return newResponse ? [...acc, newResponse] : acc
		},
		[]
	)
	if (result.length === 0) {
		return null
	}
	return result
}

function isTextResponse(
	message: PullingLeaveQueuePayload | PullingTextPayload
): message is PullingTextPayload {
	return message.type === ResponseType.Text
}

export const getAgentResponseMessages = ({
	response,
	isLastAgentMessage,
	isFirstAgentMessage,
}: {
	response: MessagePullingResponse
	isLastAgentMessage: boolean
	isFirstAgentMessage: boolean
}): AgentMessage[] | null => {
	const fulfillmentMessages = response.queryResult.fulfillmentMessages

	if (fulfillmentMessages.length > 0) {
		return fulfillmentMessages.reduce<AgentMessage[]>(
			(acc, fulfillmentMessage) => {
				const messageContent = fulfillmentMessage.payload
				let newMessage: AgentMessage | null = null

				if (isTextResponse(messageContent)) {
					if (messageContent.responses[0].text) {
						newMessage = {
							isFirstAgentMessage,
							isLastAgentMessage,
							showAvatar: true,
							value: messageContent.responses[0].text,
							role: MessageRole.Agent,
							type: MessageType.AgentText,
						}
					}
				} else {
					newMessage = {
						elements: {
							title: messageContent.responses[0].elements[0].title,
						},
						showAvatar: false,
						role: MessageRole.Agent,
						type: MessageType.LeaveAgentQueue,
					}
				}

				if (newMessage) {
					return [...acc, newMessage]
				}

				return acc
			},
			[]
		)
	}

	return null
}
